/**
 * 業務メールテンプレート (mail_templates_business) の型定義。
 *
 * 仕様: 代案 E - 業務メール自動通知ルール + 文面テンプレ編集UI
 *
 * イベント駆動でメール送信。各イベント = 1 テンプレート (重複なし、eventType を主キー的に扱う)。
 * 編集UI (/mail/business-templates) でユーザーが文面を後日修正・最適化できる。
 */
import type { IsoOrTimestamp } from './common';

/**
 * 業務メールイベント種別。
 *
 * - briefing_applied: 個別説明申込受付 → 候補枠の担当者 (GoEnHubユーザー) に通知 (2026-07-23 追加)
 * - briefing_confirmed: 個別説明日程確定 → 顧客に Zoom URL/場所案内
 * - quote_sent: 見積送付 (Phase 6 の手動送信を置換)
 * - invoice_sent: 請求送付 (請求書を手動メール送信)
 * - deal_won: 成約確定 → 顧客にサンクス
 * - invoice_paid: 入金確認 → 顧客にサンクス
 * - shipped: 出荷 → 顧客に追跡番号案内
 * - delivered: 納品完了 → 顧客に検収依頼
 * - document_auto_reply: 資料請求自動返信 (Phase 8 既存を置換)
 */
export type BusinessMailEvent =
  | 'briefing_applied'
  | 'briefing_confirmed'
  | 'quote_sent'
  | 'invoice_sent'
  | 'deal_won'
  | 'invoice_paid'
  | 'shipped'
  | 'delivered'
  | 'document_auto_reply'
  // v3.1.8 (Phase C / TimeWaver 発注フロー)
  | 'po_quote_request'    // メーカー宛の見積依頼メール
  | 'po_order_request'    // メーカー宛の正式発注メール（送金完了後の連絡）
  | 'po_transfer_request' // 経理宛: メーカー製品代金の送金申請（v3.2.22 で報酬支払い申請を分離）
  // v3.2.13.5 (報酬支払い完了通知)
  | 'commission_payment_completed' // SA/社員/紹介者宛の「報酬の振込完了」お知らせ
  // v3.2.14 (Phase 3a/3b - SA 主導の請求書発行フロー)
  | 'commission_invoice_received' // 経理宛: SA から請求書届きました (公開URL付き)
  | 'commission_invoice_paid' // SA 宛: 請求書全体の振込完了 (含まれる record 全体)
  | 'commission_invoice_reminder' // SA 宛: 管理者からの「請求してください」リマインダー
  // v3.2.22 (整合性 / Phase 1+2+3.1)
  | 'commission_payment_request'    // 経理宛: 報酬支払い申請 (旧 po_transfer_request の流用を解消)
  | 'form_submitted'                // 顧客向けサンクス + 営業向け Chat 通知
  | 'deal_lost'                     // 営業向け失注通知
  | 'commission_record_created'    // SA 向け「報酬が発生しました」通知
  // v3.2.25 (同意ステップ / Phase 3)
  | 'consent_send'                 // 顧客宛: 同意書送付 (公開リンク + サイン依頼)
  | 'consent_signed'               // 営業宛: 同意書サイン提出されました
  // v3.2.30 (外部SA管理: ステータス遷移ごとの自動通知)
  | 'sa_provisional'               // SA 宛: 仮登録 (申請受付 + 同意書サイン/登録費の案内)
  | 'sa_registered'                // SA 宛: 登録完了 (契約開始日/終了日 + 説明会案内)
  | 'sa_active'                    // SA 宛: 活動可能 (受講完了 → 活動開始の案内)
  // v3.2.30 (外部SA管理: 契約更新)
  | 'sa_renewal_reminder'          // SA 宛: 契約終了 N日前の更新案内 (更新料の案内)
  | 'sa_renewal_due_internal'      // 管理者宛: 更新対応が必要 (期限接近 / SA が更新申請)
  | 'sa_renewed'                   // SA 宛: 更新完了 (新契約期間)
  // 2026-07-28 (SA 顧客保有期間)
  | 'sa_customer_retention_reminder'; // SA 宛: 担当顧客の保有期限が近い (入金がなければ外れる)

export const BUSINESS_MAIL_EVENT_LABEL: Record<BusinessMailEvent, string> = {
  briefing_applied: '【個別説明】申込受付 (担当者宛)',
  briefing_confirmed: '個別説明日程確定',
  quote_sent: '見積送付',
  invoice_sent: '請求送付',
  deal_won: '成約確定',
  invoice_paid: '入金確認',
  shipped: '出荷通知',
  delivered: '納品完了 / 検収依頼',
  document_auto_reply: '資料請求 自動返信',
  po_quote_request: '【発注】メーカー見積依頼',
  po_order_request: '【発注】メーカー正式発注',
  po_transfer_request: '【発注】経理 送金申請',
  commission_payment_completed: '【報酬】支払い完了通知',
  commission_invoice_received: '【報酬】SA 請求書受領 (経理宛)',
  commission_invoice_paid: '【報酬】SA 請求書 一括振込完了',
  commission_invoice_reminder: '【報酬】請求書発行リマインダー',
  // v3.2.22
  commission_payment_request: '【報酬】支払い申請 (経理宛)',
  form_submitted: '【受付】フォーム送信',
  deal_lost: '【商談】失注確定',
  commission_record_created: '【報酬】報酬発生のお知らせ (SA 宛)',
  // v3.2.25 (同意ステップ)
  consent_send: '【同意書】送付 (顧客宛)',
  consent_signed: '【同意書】サイン提出 (営業宛)',
  // v3.2.30 (外部SA管理)
  sa_provisional: '【SA】仮登録のご案内',
  sa_registered: '【SA】登録完了のお知らせ',
  sa_active: '【SA】活動開始のご案内',
  sa_renewal_reminder: '【SA】契約更新のご案内 (期限前)',
  sa_renewal_due_internal: '【SA】更新対応のお知らせ (管理者宛)',
  sa_renewed: '【SA】契約更新完了のお知らせ',
  sa_customer_retention_reminder: '【SA】担当顧客の保有期限が近づいています',
};

/**
 * テンプレ本文・件名で利用可能な変数。
 * イベントごとに利用可能な変数セットが異なる。UI ヘルプ表示用。
 */
export const BUSINESS_MAIL_AVAILABLE_VARS: Record<BusinessMailEvent, string[]> = {
  briefing_applied: ['assigneeName', 'customerName', 'customerKana', 'customerEmail', 'companyName', 'inquiryNotes', 'candidateDates', 'participantCount', 'formTitle', 'adminUrl', 'vendorName'],
  briefing_confirmed: ['customerName', 'companyName', 'date', 'startTime', 'endTime', 'style', 'zoomUrl', 'location', 'assigneeName', 'participantCount', 'vendorName'],
  quote_sent: ['customerName', 'companyName', 'quoteTitle', 'totalAmount', 'validUntil', 'publicUrl', 'vendorName'],
  invoice_sent: ['customerName', 'companyName', 'invoiceNumber', 'invoiceTitle', 'amount', 'dueDate', 'publicUrl', 'vendorName'],
  deal_won: ['customerName', 'companyName', 'dealTitle', 'finalAmount', 'vendorName'],
  invoice_paid: ['customerName', 'companyName', 'invoiceNumber', 'amount', 'vendorName'],
  shipped: ['customerName', 'companyName', 'deliveryNumber', 'carrierName', 'trackingNumber', 'vendorName'],
  delivered: ['customerName', 'companyName', 'deliveryNumber', 'productSummary', 'vendorName'],
  document_auto_reply: ['customerName', 'companyName', 'documentTitle', 'publicUrl', 'downloadUrl', 'passwordHint', 'password', 'validUntil', 'authMode', 'documentType', 'vendorName'],
  po_quote_request: ['customerName', 'dealTitle', 'orderedProducts', 'productSummary', 'assigneeName', 'vendorName'],
  po_order_request: ['customerName', 'invoiceNumber', 'orderedProducts', 'totalAmount', 'discountAmount', 'finalAmount', 'assigneeName', 'vendorName'],
  po_transfer_request: ['customerName', 'invoiceNumber', 'totalAmount', 'discountAmount', 'finalAmount', 'memo', 'assigneeName', 'vendorName'],
  commission_payment_completed: ['recipientName', 'dealTitle', 'productName', 'amount', 'bankInfo', 'paidOutAt', 'contactName', 'vendorName'],
  commission_invoice_received: ['recipientName', 'invoiceNumber', 'periodLabel', 'subtotal', 'tax', 'total', 'publicUrl', 'lineCount', 'vendorName', 'fallbackBody'],
  commission_invoice_paid: ['recipientName', 'invoiceNumber', 'total', 'paidOutAt', 'bankInfo', 'lineCount', 'vendorName', 'fallbackBody'],
  commission_invoice_reminder: ['recipientName', 'pendingCount', 'totalAmount', 'invoicePageUrl', 'vendorName', 'fallbackBody'],
  // v3.2.22
  commission_payment_request: ['recipientName', 'recipientType', 'dealTitle', 'contactName', 'productName', 'amount', 'formula', 'bankInfo', 'paidAt', 'commissionSetName', 'vendorName', 'fallbackBody'],
  form_submitted: ['customerName', 'customerEmail', 'formTitle', 'formCategory', 'submittedAt', 'adminUrl', 'vendorName'],
  deal_lost: ['dealTitle', 'customerName', 'companyName', 'lostReason', 'lostQuoteId', 'assigneeName', 'expectedAmount', 'adminUrl', 'vendorName'],
  commission_record_created: ['recipientName', 'dealTitle', 'contactName', 'productName', 'amount', 'commissionSetName', 'paidAt', 'vendorName'],
  // v3.2.25 (同意ステップ)
  consent_send: ['customerName', 'companyName', 'consentTitle', 'publicUrl', 'dealTitle', 'vendorName'],
  consent_signed: ['customerName', 'companyName', 'dealTitle', 'consentTitle', 'signedAt', 'adminUrl', 'vendorName'],
  // v3.2.30 (外部SA管理) — footer は会社情報から自動組立、fee は登録料/更新料 (設定値)
  sa_provisional: ['saName', 'appliedAt', 'fee', 'vendorName', 'footer'],
  sa_registered: ['saName', 'contractStartDate', 'contractEndDate', 'contractTerm', 'vendorName', 'footer'],
  sa_active: ['saName', 'contractStartDate', 'contractEndDate', 'contractTerm', 'vendorName', 'footer'],
  sa_renewal_reminder: ['saName', 'contractEndDate', 'contractTerm', 'fee', 'daysLeft', 'vendorName', 'footer'],
  sa_renewal_due_internal: ['saName', 'saEmail', 'contractEndDate', 'contractTerm', 'daysLeft', 'reason', 'adminUrl', 'vendorName'],
  sa_renewed: ['saName', 'contractStartDate', 'contractEndDate', 'contractTerm', 'vendorName', 'footer'],
  // 2026-07-28 (SA 顧客保有期間) — customerList は「顧客名 (期限日)」の複数行テキスト
  sa_customer_retention_reminder: ['saName', 'customerCount', 'customerList', 'retentionMonths', 'daysLeft', 'saPortalUrl', 'vendorName', 'footer'],
};

/**
 * @deprecated v3.2.21 で `BusinessNotificationTemplate` に統合。
 * 旧構造 (Top-level subject/body) のままの未マイグレ docs 用に型を残置するのみ。
 * 新規コードは `BusinessNotificationTemplate` を使うこと。
 */
export interface BusinessMailTemplate {
  /** 主キー的に扱う。イベント種別 (1イベント = 1テンプレ) */
  eventType: BusinessMailEvent;
  /** false なら送信スキップ (テンプレ自体は残す) */
  enabled: boolean;
  /** 件名。{{customerName}} 形式で変数差し込み */
  subject: string;
  /** 本文。{{xxx}} 形式で変数差し込み */
  body: string;
  /** 送信元 mail_accounts/{accountId}。null = 既定アカウント */
  fromAccountId?: string | null;
  /** 利用可能な変数の参考リスト (UIヘルプ用、フィールドとしては optional) */
  availableVars?: string[];
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

// ─────────────────────────────────────────────
// v3.2.21: メール+Google Chat を 1 ドキュメントで統合管理する新スキーマ
// ─────────────────────────────────────────────

/**
 * メール部分の設定。
 * 旧 BusinessMailTemplate の `enabled / subject / body / fromAccountId` を `mail.*` 配下に移動。
 */
export interface BusinessNotificationMailSection {
  /** true = 自動送信する。false でメール送信のみスキップ (chat には影響しない) */
  enabled: boolean;
  /** 件名。{{var}} 形式で差し込み */
  subject: string;
  /** 本文。{{var}} 形式で差し込み */
  body: string;
  /** 送信元 mail_accounts/{accountId}。null = 既定 (resolveAutoMailAccountId に委譲) */
  fromAccountId?: string | null;
}

/** チャット通知のフォーマット種別。 */
export type BusinessChatFormat = 'text' | 'card';

/** カード通知 1 件分のリンクボタン (Google Chat Card v2 の Button)。 */
export interface BusinessChatButton {
  /** 表示ラベル (例: "管理画面で詳細を確認") */
  label: string;
  /** リンク先 URL。{{var}} 形式で差し込み可能 */
  url: string;
}

/**
 * カード形式のペイロード。
 *
 * Google Chat の Cards V2 を簡略化したスキーマ。
 *  - title: ヘッダタイトル (例: "🔔 新規先行予約")
 *  - subtitle: 送信元アプリ名相当 (例: "TimeWaverHub")
 *  - body: 本文 (複数行可、{{var}} 形式)
 *  - buttons: リンクボタン (0〜5 個)
 */
export interface BusinessChatCardPayload {
  title: string;
  subtitle?: string | null;
  body: string;
  buttons?: BusinessChatButton[];
}

/**
 * チャット部分の設定。
 *
 * - type='text': シンプル text (text フィールド) を送る
 * - type='card': Cards V2 形式 (card フィールド) を送る
 */
export interface BusinessNotificationChatSection {
  /** true = 自動送信する */
  enabled: boolean;
  /** フォーマット種別 */
  type: BusinessChatFormat;
  /** type='text' のとき使う本文。{{var}} 形式 */
  text?: string | null;
  /** type='card' のとき使うカードペイロード */
  card?: BusinessChatCardPayload | null;
  /** 送信先 chat_endpoints/{endpointId}。null は未設定 (送信スキップ) */
  endpointId?: string | null;
}

/**
 * 業務通知テンプレート (v3.2.21+)。
 *
 * Firestore コレクション: `mail_templates_business`（既存名のまま、互換性のため）
 * Document ID = eventType (1 イベント = 1 テンプレ)
 *
 * 旧スキーマ (`BusinessMailTemplate`) からのマイグレ:
 *  - subject/body/enabled/fromAccountId → mail.* 配下にネスト
 *  - chat セクションは空 (`{ enabled: false, type: 'text', text: '', endpointId: null }`) で初期化
 *
 * 自動送信ロジック:
 *  - イベント発火時、mail.enabled が true なら renderAndSendBusinessMail を呼ぶ
 *  - 同タイミングで chat.enabled が true なら dispatchBusinessChatNotification を呼ぶ
 *  - 失敗してもメイン処理は止めない
 */
/**
 * テンプレートの宛先カテゴリ (2026-05-28 追加)。
 * - 'customer': お客様・SA 宛 (商談・請求書・同意書など外向き)
 * - 'internal': 社内オペレーション宛 (経理・メーカー・営業担当)
 *
 * 送信制御には使わず、UI バッジ表示のみ。実際の宛先は呼び出し側 (各 Cloud Function) が `to` 引数で指定する。
 */
export type BusinessNotificationAudience = 'customer' | 'internal';

export interface BusinessNotificationTemplate {
  /**
   * イベント種別 (トリガー)。
   *
   * 2026-05-28 拡張:
   *  - null = 「未アサイン」(どのトリガーでも発火しない)
   *  - 値あり = そのイベント発火時に送信候補となる
   *
   * 同じ eventType に対して複数テンプレが存在し得る (UUID docID で複製可能)。
   * 既存の eventType-as-docID なドキュメント (Phase 24h 以前) との互換性は維持。
   */
  eventType: BusinessMailEvent | null;
  /**
   * 2026-05-28 追加: UI 一覧での表示ラベル (識別用)。
   * 同じ eventType に複数テンプレが存在する場合の区別に使う。
   * 未設定の場合は eventType 既定ラベル + applicable カテゴリ名を組み合わせて自動表示。
   */
  label?: string | null;
  /** メール送信ルール */
  mail: BusinessNotificationMailSection;
  /** チャット通知ルール */
  chat: BusinessNotificationChatSection;
  /** 利用可能な変数の参考リスト (UI ヘルプ用) */
  availableVars?: string[];
  /**
   * 2026-05-28 追加: 適用対象の主軸カテゴリ ID 配列。
   * - undefined / null / 空配列 → 「共通」(全カテゴリで送信)
   * - 値あり → 指定カテゴリの Deal のみで送信
   *
   * 同 eventType + applicable 重複時は upsert で自動解消される
   * (既存テンプレから該当カテゴリを除外、空になれば eventType=null へ)。
   */
  applicableCategoryIds?: string[] | null;
  /**
   * 2026-05-28 追加: UI バッジ表示用の宛先カテゴリ。
   * 既定値は EVENT_AUDIENCE_DEFAULTS マップで eventType から自動推定。
   * 値が無い場合 UI 側で既定値を使う。
   */
  audience?: BusinessNotificationAudience;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}

/**
 * 2026-05-28 追加: eventType ごとの既定 audience。
 * テンプレに `audience` が未設定の場合、UI はこれをフォールバックする。
 */
export const EVENT_AUDIENCE_DEFAULTS: Record<
  BusinessMailEvent,
  BusinessNotificationAudience
> = {
  // 顧客 / SA 宛 (外向き)
  briefing_confirmed: 'customer',
  // 社内 (候補枠の担当者宛)
  briefing_applied: 'internal',
  quote_sent: 'customer',
  invoice_sent: 'customer',
  deal_won: 'customer',
  invoice_paid: 'customer',
  shipped: 'customer',
  delivered: 'customer',
  document_auto_reply: 'customer',
  form_submitted: 'customer',
  consent_send: 'customer',
  commission_payment_completed: 'customer', // SA / 紹介者宛
  commission_invoice_paid: 'customer', // SA 宛
  commission_invoice_reminder: 'customer', // SA 宛
  commission_record_created: 'customer', // SA 宛
  // v3.2.30 (外部SA管理) — SA 宛 (外向き)
  sa_provisional: 'customer',
  sa_registered: 'customer',
  sa_active: 'customer',
  sa_renewal_reminder: 'customer',
  sa_renewed: 'customer',
  sa_customer_retention_reminder: 'customer', // SA 宛
  // 社内オペレーション宛 (経理・メーカー・営業)
  po_quote_request: 'internal',
  po_order_request: 'internal',
  po_transfer_request: 'internal',
  commission_invoice_received: 'internal', // 経理宛
  commission_payment_request: 'internal', // 経理宛
  deal_lost: 'internal', // 営業宛
  consent_signed: 'internal', // 営業宛
  sa_renewal_due_internal: 'internal', // 管理者宛
};

/**
 * 2026-05-28 追加: eventType ごとの送信タイミングを人間が読める短文で説明。
 * UI のテンプレリストに「いつ送られるか」を明示するための表示用ラベル。
 * 仕様変更時は本マップを更新する。実際のトリガーは各 Cloud Function 側に
 * ハードコードされているため、本マップが唯一の真実ではない (表示のための要約)。
 */
export const EVENT_TIMING_LABEL: Record<BusinessMailEvent, string> = {
  briefing_applied: '個別説明フォーム申込時 (候補枠の担当者へ)',
  briefing_confirmed: '個別説明スケジュール確定時',
  quote_sent: '見積書を発行 (送信オプション有効) 時',
  invoice_sent: '請求書をメール送信時',
  deal_won: '商談が成約 (won) 確定時',
  invoice_paid: '請求書が入金確認 (paid) された時',
  shipped: '出荷ステータスが「出荷済」に遷移時',
  delivered: '出荷ステータスが「納品完了」に遷移時',
  document_auto_reply: '資料請求フォーム送信時',
  po_quote_request: 'メーカー見積依頼を発注時',
  po_order_request: 'メーカーへ正式発注時 (送金完了後)',
  po_transfer_request: '経理へメーカー送金申請時',
  commission_payment_completed: '報酬振込が完了 (paid) した時',
  commission_invoice_received: 'SA が請求書を発行した時 (経理通知)',
  commission_invoice_paid: 'SA 請求書一括振込完了時',
  commission_invoice_reminder: '管理者が「請求してください」を送信時',
  commission_payment_request: '経理に報酬支払い申請時',
  form_submitted: 'フォーム送信時 (顧客 + 社内 Chat)',
  deal_lost: '商談が失注 (lost) 確定時',
  commission_record_created: '報酬レコード生成時 (SA に通知)',
  consent_send: '同意書を送付時',
  consent_signed: '同意書がサイン提出された時',
  sa_provisional: 'SA が招待を受けて仮登録された時',
  sa_registered: 'SA が登録完了 (同意 + 入金) に遷移した時',
  sa_active: 'SA が活動可能 (受講完了) に遷移した時',
  sa_renewal_reminder: '契約終了 N日前 (日次バッチ) に SA へ',
  sa_renewal_due_internal: '更新期限接近 / SA が更新申請した時に管理者へ',
  sa_renewed: '管理者が更新を確定し再び活動可能になった時',
  sa_customer_retention_reminder:
    '担当顧客の保有期限が近づいた時 (日次バッチ。SA ごとに 1 通へまとめて送信)',
};

/**
 * テンプレ送信時のコンテキスト変数 (イベント発火時に組み立てて差し込む)。
 * 値が undefined のキーは空文字に置換する。
 */
export type BusinessMailVars = Record<string, string | number | null | undefined>;
